// FairPriceDrawer — full intelligence layer for one fixture. Markup
// follows the prototype at UI Refactor/Build/fair-price.jsx: .fp-section
// blocks for the independent model (xG → Poisson → fair odds) and per
// market breakdown (.fp-adv-grid: Match Winner / BTTS / Over-Under).
//
// All numbers come from useFairPriceOutputs (fair_price_outputs +
// clv_consensus). xG lambdas are derived from the engine's stored
// model_inputs (home_rating, away_rating, goals_per_game, home_adv_elo)
// using the standard ELO→expected-goals share formula. The Poisson
// grid uses src/fair-price/scoreline-grid.ts (real math, not mock).
//
// No mockdata from the prototype's FP_DATA stub list is introduced.
// No fictional briefing copy. Empty-state text replaces sections with
// no underlying data.

import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { PoissonScoreMatrix } from "../../components/exchange/PoissonScoreMatrix.tsx";
import { PaywallCard } from "../../components/PaywallCard.tsx";
import { useUserTier } from "../../auth/useUserTier.ts";
import { useFixtureAttention } from "../../exchange/useFixtureAttention.ts";
import { attentionTier } from "../../exchange/fixtureAttention.ts";
import { marketEyebrow, renderLegSelection } from "../../exchange/leg-format.ts";
import {
  buildScorelineGrid,
  topScorelines,
} from "../../fair-price/scoreline-grid.ts";
import type {
  FairPriceFixture,
  FairPriceRow,
} from "../../exchange/useFairPriceOutputs.ts";
import {
  classifyHold,
  marketHoldPct,
  type HoldClass,
} from "../../exchange/oddsComparison.ts";
import {
  priceAsianHandicap,
  priceTotalLine,
  priceFirstHalf1X2,
} from "../../fair-price/derivatives.ts";
import type { ScorelineGridResult } from "../../fair-price/scoreline-grid.ts";

export interface FairPriceDrawerProps {
  readonly fixture: FairPriceFixture;
  readonly onClose: () => void;
}

interface DerivedLambdas {
  home: number;
  away: number;
  total: number;
}

function num(v: unknown, fallback: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

// Derive home/away expected goals from the ELO + goals-per-game stored
// in fair_price_outputs.model_inputs. Standard ELO→share formula:
//   share_home = (home + home_adv) / (home + home_adv + away)
//   λ_home = goals_per_game · share_home
//   λ_away = goals_per_game − λ_home
function deriveLambdas(modelInputs: Record<string, unknown>): DerivedLambdas {
  const homeRating = num(modelInputs.home_rating, 1500);
  const awayRating = num(modelInputs.away_rating, 1500);
  const homeAdv = num(modelInputs.home_adv_elo, 65);
  const goalsPerGame = num(modelInputs.goals_per_game, 2.6);
  const denom = homeRating + homeAdv + awayRating;
  const shareHome = denom > 0 ? (homeRating + homeAdv) / denom : 0.5;
  const home = Math.max(0, goalsPerGame * shareHome);
  const away = Math.max(0, goalsPerGame - home);
  return { home, away, total: home + away };
}

// FPE-CARD (2026-05-29) — delegate to the shared formatter so every
// market family the engine writes (AH, halves, OR-combos, tournament-
// context, corners) renders cleanly. The old 3-market/7-selection
// local dictionaries silently fell through to raw underscore tokens.

function marketLabel(m: string): string {
  return marketEyebrow(m);
}

function selLabel(market: string, selection: string, fix: FairPriceFixture): string {
  return renderLegSelection(market, selection, {
    home_team: fix.home_team,
    away_team: fix.away_team,
  });
}

function fmtPct(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return "—";
  return `${(p * 100).toFixed(1)}%`;
}

function fmtSignedPct(p: number | null): string {
  if (p === null || !Number.isFinite(p)) return "—";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(1)}%`;
}

function fmtOdds(o: number | null): string {
  if (o === null || !Number.isFinite(o)) return "—";
  return o.toFixed(2);
}

function fmtKick(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function recommendationFor(edge: number | null): {
  cls: "take" | "watch" | "pass";
  lab: "TAKE" | "WATCH" | "PASS";
} {
  if (edge === null) return { cls: "watch", lab: "WATCH" };
  if (edge >= 5) return { cls: "take", lab: "TAKE" };
  if (edge >= 0) return { cls: "watch", lab: "WATCH" };
  return { cls: "pass", lab: "PASS" };
}

const HOLD_META: Record<HoldClass, { label: string; color: string }> = {
  efficient: { label: "EFFICIENT", color: "var(--accent-positive, #2ecc71)" },
  standard: { label: "STANDARD", color: "var(--text-tertiary, #888)" },
  exploitative: { label: "WIDE", color: "var(--accent-negative, #e74c3c)" },
};

function HoldChip({ hold }: { hold: number | null }): JSX.Element | null {
  const cls = classifyHold(hold);
  // Honest absence: incomplete market ⇒ no chip at all (never 0%).
  if (cls === null || hold === null) return null;
  const meta = HOLD_META[cls];
  return (
    <span
      data-testid="fp-hold-chip"
      data-hold-class={cls}
      title="Bookmaker hold (overround) on this market — what the book extracts"
      style={{
        marginLeft: "auto",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.06em",
        color: meta.color,
        border: `1px solid ${meta.color}`,
        borderRadius: 3,
        padding: "1px 6px",
      }}
    >
      HOLD {(hold * 100).toFixed(1)}% · {meta.label}
    </span>
  );
}

function DerivRow({
  label,
  a,
  b,
}: {
  label: string;
  a: { tag: string; odds: number | null };
  b: { tag: string; odds: number | null };
}): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.3fr 1fr 1fr",
        gap: 8,
        padding: "7px 0",
        borderBottom: "1px solid var(--border-faint)",
        alignItems: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
      }}
    >
      <div style={{ color: "var(--text-primary)" }}>{label}</div>
      <div>
        <span style={{ color: "var(--text-tertiary)" }}>{a.tag} </span>
        {a.odds === null ? "—" : a.odds.toFixed(2)}
      </div>
      <div>
        <span style={{ color: "var(--text-tertiary)" }}>{b.tag} </span>
        {b.odds === null ? "—" : b.odds.toFixed(2)}
      </div>
    </div>
  );
}

// F4 — derivatives are a different marginalisation of the SAME grid:
// no new model, no fabricated price (null odds when unsupported).
function DerivativeMarkets({
  fixture,
  grid,
  lambdas,
}: {
  fixture: FairPriceFixture;
  grid: ScorelineGridResult;
  lambdas: DerivedLambdas;
}): JSX.Element {
  const ahLines = [-1, -0.75, -0.5, 0.5, 1];
  const totalLines = [1.5, 2.5, 2.75, 3.5];
  const fh = priceFirstHalf1X2(lambdas.home, lambdas.away);
  return (
    <div className="fp-adv">
      <div className="fp-adv-h" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="trend" size={13} />
        <span>Asian handicap — {fixture.home_team}</span>
      </div>
      <div className="fp-adv-desc" style={{ paddingTop: 6 }}>
        {ahLines.map((l) => {
          const p = priceAsianHandicap(grid, l);
          const sign = l > 0 ? `+${l}` : `${l}`;
          return (
            <DerivRow
              key={`ah${l}`}
              label={`Handicap ${sign}`}
              a={{ tag: "home", odds: p.homeFairOdds }}
              b={{ tag: "away", odds: p.awayFairOdds }}
            />
          );
        })}
      </div>
      <div
        className="fp-adv-h"
        style={{ display: "flex", alignItems: "center", gap: 6, paddingTop: 10 }}
      >
        <Icon name="trend" size={13} />
        <span>Alternate totals</span>
      </div>
      <div className="fp-adv-desc" style={{ paddingTop: 6 }}>
        {totalLines.map((l) => {
          const p = priceTotalLine(grid, l);
          return (
            <DerivRow
              key={`tot${l}`}
              label={`Total ${l.toFixed(2)}`}
              a={{ tag: "over", odds: p.homeFairOdds }}
              b={{ tag: "under", odds: p.awayFairOdds }}
            />
          );
        })}
      </div>
      <div
        className="fp-adv-h"
        style={{ display: "flex", alignItems: "center", gap: 6, paddingTop: 10 }}
      >
        <Icon name="trend" size={13} />
        <span>First half 1X2</span>
        <span
          style={{
            marginLeft: "auto",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-tertiary)",
          }}
          title="First-half goal-share modelling assumption (explicit, overridable)"
        >
          ½-share {fh.halfShare}
        </span>
      </div>
      <div
        className="fp-adv-desc"
        style={{
          paddingTop: 6,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        {[
          { lab: fixture.home_team, o: fh.homeFairOdds },
          { lab: "Draw", o: fh.drawFairOdds },
          { lab: fixture.away_team, o: fh.awayFairOdds },
        ].map((c) => (
          <div key={c.lab}>
            <div style={{ color: "var(--text-tertiary)", fontSize: 10 }}>{c.lab}</div>
            <div style={{ color: "var(--text-primary)" }}>
              {c.o === null ? "—" : c.o.toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketSection({
  fixture,
  rows,
  marketKey,
}: {
  fixture: FairPriceFixture;
  rows: readonly FairPriceRow[];
  marketKey: string;
}): JSX.Element | null {
  const subset = rows.filter((r) => r.market === marketKey);
  if (subset.length === 0) return null;
  // F3: hold computed from the bookmaker prices of THIS market's
  // selections. Incomplete ⇒ null ⇒ chip omitted (no fabrication).
  const hold = marketHoldPct(subset.map((r) => r.bookmaker_odds));
  return (
    <div className="fp-adv">
      <div className="fp-adv-h" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Icon name="trend" size={13} />
        <span>{marketLabel(marketKey)}</span>
        <HoldChip hold={hold} />
      </div>
      <div className="fp-adv-desc" style={{ paddingTop: 6 }}>
        {subset.map((r) => {
          const edge = r.edge_pct;
          const cls = edge === null ? "neu" : edge > 0 ? "pos" : edge < 0 ? "neg" : "neu";
          return (
            <div
              key={`${r.market}-${r.selection}`}
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr 1fr 1fr",
                gap: 8,
                padding: "8px 0",
                borderBottom: "1px solid var(--border-faint)",
                alignItems: "center",
              }}
            >
              <div style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                {selLabel(r.market, r.selection, fixture)}
              </div>
              <div style={{ fontFamily: "var(--font-mono)" }}>
                fair {fmtOdds(r.fair_odds)}
                <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                  {fmtPct(r.fair_prob)}
                </div>
              </div>
              <div style={{ fontFamily: "var(--font-mono)" }}>
                mkt {fmtOdds(r.bookmaker_odds)}
                <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                  {r.bookmaker_prob !== null ? fmtPct(r.bookmaker_prob) : "—"}
                </div>
              </div>
              <div className={`fp-edge`} style={{ textAlign: "right" }}>
                <span className={`num ${cls}`}>{fmtSignedPct(edge)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FairPriceDrawer({ fixture, onClose }: FairPriceDrawerProps): JSX.Element {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const { hasTier, loading: tierLoading } = useUserTier();
  const isPro = hasTier("sika_pro");
  // F8 — anonymised crowd-attention. Dormant by design: null for
  // almost every fixture today ⇒ the chip simply does not render
  // (no fabricated attention).
  const { forFixture: attentionFor } = useFixtureAttention();
  const attention = attentionFor(fixture.fixture_id);
  const attTier = attentionTier(attention);

  useEffect(() => {
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const head = fixture.headline_row;
  const lambdas = useMemo(() => deriveLambdas(head.model_inputs), [head.model_inputs]);
  const grid = useMemo(
    () => buildScorelineGrid(lambdas.home, lambdas.away, 6),
    [lambdas.home, lambdas.away],
  );
  const top = useMemo(() => topScorelines(grid, 6), [grid]);
  // GAP-05 — Pro+ score-matrix transparency, collapsed by default.
  const [showMatrix, setShowMatrix] = useState(false);

  const rec = recommendationFor(head.edge_pct);

  return (
    <>
      <div className="fp-scrim" onClick={onClose} />
      <div className="fp-drawer" role="dialog" aria-label="Fair Price intelligence">
        <div className="fp-head">
          <div className="fp-eyebrow">
            <span className="fp-tag">FAIR PRICE INTEL</span>
            <span className="fp-id">{fixture.fixture_id}</span>
            <button
              ref={closeRef}
              className="fp-close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              <Icon name="close" size={16} />
            </button>
          </div>
          <div className="fp-fixture">
            <h2>
              {fixture.home_team} <span className="vs">vs</span> {fixture.away_team}
            </h2>
          </div>
          <div className="fp-meta">
            <span>{fixture.competition}</span>
            <span className="dot" />
            <span>{fmtKick(fixture.kickoff_at)}</span>
            <span className="dot" />
            <span>{marketLabel(head.market)}</span>
            {attention && attTier ? (
              <>
                <span className="dot" />
                <span
                  data-testid="fp-attention-chip"
                  data-attention-tier={attTier}
                  title="Anonymised crowd attention (counts only). Review = flagged for a model confidence review."
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.06em",
                    color:
                      attTier === "review"
                        ? "var(--accent-negative, #e74c3c)"
                        : "var(--text-tertiary, #888)",
                    border: `1px solid ${
                      attTier === "review"
                        ? "var(--accent-negative, #e74c3c)"
                        : "var(--border-faint, #444)"
                    }`,
                    borderRadius: 3,
                    padding: "1px 6px",
                  }}
                >
                  {attTier === "review"
                    ? `CROWD REVIEW · ${attention.watchers}`
                    : `CROWD · ${attention.watchers}`}
                </span>
              </>
            ) : null}
          </div>
        </div>

        <div className={`fp-recommend ${rec.cls}`}>
          <div className="fp-rec-left">
            <div className="fp-rec-lab">STRIKZA RECOMMENDATION</div>
            <div className="fp-rec-val">{rec.lab}</div>
            <div className="fp-rec-sub">
              Headline selection · {selLabel(head.market, head.selection, fixture)}
            </div>
          </div>
          <div className="fp-rec-prices">
            <div className="fp-price">
              <div className="lab">FAIR PRICE</div>
              <div className="val">{fmtOdds(head.fair_odds)}</div>
              <div className="sub">{fmtPct(head.fair_prob)} true prob</div>
            </div>
            <div className="fp-price-arrow">→</div>
            <div className="fp-price market">
              <div className="lab">MARKET PRICE</div>
              <div className="val">{fmtOdds(head.bookmaker_odds)}</div>
              <div className="sub">
                {head.bookmaker_prob !== null ? `${fmtPct(head.bookmaker_prob)} implied` : "no consensus"}
              </div>
            </div>
            <div className="fp-edge">
              <div className="lab">EDGE</div>
              <div className="val">{fmtSignedPct(head.edge_pct)}</div>
            </div>
          </div>
        </div>

        {/* Layer 1 — Independent model */}
        <div className="fp-section">
          <div className="fp-h">
            <span className="num">L1</span>
            <h3>Independent probability model</h3>
            <span className="fp-hsub">xG → Poisson → fair odds</span>
          </div>
          <div className="fp-xg-grid">
            <div className="fp-xg-card">
              <div className="lab">Home xG</div>
              <div className="val">{lambdas.home.toFixed(2)}</div>
              <div className="bar">
                <div className="fill" style={{ width: `${(lambdas.home / 4) * 100}%` }} />
              </div>
            </div>
            <div className="fp-xg-card">
              <div className="lab">Away xG</div>
              <div className="val">{lambdas.away.toFixed(2)}</div>
              <div className="bar">
                <div className="fill" style={{ width: `${(lambdas.away / 4) * 100}%` }} />
              </div>
            </div>
            <div className="fp-xg-card">
              <div className="lab">Total xG</div>
              <div className="val">{lambdas.total.toFixed(2)}</div>
              <div className="sub">
                derived from goals/game {num(head.model_inputs.goals_per_game, 2.6).toFixed(2)}
              </div>
            </div>
            <div className="fp-xg-card">
              <div className="lab">Home rating</div>
              <div className="val">{Math.round(num(head.model_inputs.home_rating, 1500))}</div>
              <div className="sub">ELO + {Math.round(num(head.model_inputs.home_adv_elo, 65))} home adv</div>
            </div>
            <div className="fp-xg-card">
              <div className="lab">Away rating</div>
              <div className="val">{Math.round(num(head.model_inputs.away_rating, 1500))}</div>
              <div className="sub">ELO</div>
            </div>
          </div>

          <div className="fp-poisson">
            <div className="fp-poisson-h">Top 6 most likely scorelines</div>
            <div className="fp-poisson-bars">
              {top.map((p) => (
                <div key={`${p.home}-${p.away}`} className="fp-poisson-row">
                  <div className="score mono">
                    {p.home}-{p.away}
                  </div>
                  <div className="bar">
                    <div className="fill" style={{ width: `${p.probability * 600}%` }} />
                  </div>
                  <div className="prob mono">{(p.probability * 100).toFixed(1)}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* GAP-05 — Poisson score-matrix transparency. Additive: the
            free Top-6 above is unchanged; this deeper 5×5 model view is
            Pro+ and collapsed by default. */}
        <div className="fp-section">
          <div className="fp-h">
            <span className="num">L1+</span>
            <h3>Score matrix (model)</h3>
            <span className="fp-hsub">Poisson P(home, away), 0–4</span>
          </div>
          {tierLoading ? null : isPro ? (
            <div style={{ paddingTop: 8 }}>
              <button
                type="button"
                data-testid="poisson-matrix-toggle"
                onClick={() => setShowMatrix((v) => !v)}
                className="btn"
                style={{ marginBottom: 10 }}
              >
                {showMatrix ? "Hide score matrix" : "Show score matrix"}
              </button>
              {showMatrix ? <PoissonScoreMatrix cells={grid.cells} /> : null}
            </div>
          ) : (
            <div style={{ paddingTop: 12 }}>
              <PaywallCard
                requiredTier="sika_pro"
                featureName="Score matrix"
                pitch="See the full Poisson score-probability matrix STRIKZA's pricing engine derived for this fixture — not just the conclusion, the evidence. Available on STRIKZA Sika Pro."
              />
            </div>
          )}
        </div>

        {/* Per-market breakdown — every selection the engine scored.
            Pro+ gated (Phase H Paystack). The free tier sees the headline
            edge + the model-distribution top-6 above; the per-market
            breakdown across Match Winner / BTTS / Over-Under is a paid
            surface. */}
        <div className="fp-section">
          <div className="fp-h">
            <span className="num">L2</span>
            <h3>Per-market breakdown</h3>
            <span className="fp-hsub">every selection scored for this fixture</span>
          </div>
          {tierLoading ? null : isPro ? (
            <div className="fp-adv-grid">
              <MarketSection fixture={fixture} rows={fixture.all_rows} marketKey="match_winner" />
              <MarketSection fixture={fixture} rows={fixture.all_rows} marketKey="btts" />
              <MarketSection fixture={fixture} rows={fixture.all_rows} marketKey="over_under_2_5" />
            </div>
          ) : (
            <div style={{ paddingTop: 12 }}>
              <PaywallCard
                requiredTier="sika_pro"
                featureName="Per-market breakdown"
                pitch="See every selection STRIKZA scored for this fixture — Match Winner, BTTS, and Over/Under — with model probability, market quote, edge, and fair odds side-by-side. Available on STRIKZA Sika Pro."
              />
            </div>
          )}
        </div>

        <div className="fp-section">
          <div className="fp-h">
            <span className="num">L3</span>
            <h3>Derivative markets</h3>
            <span className="fp-hsub">priced from the same scoreline grid</span>
          </div>
          {tierLoading ? null : isPro ? (
            <div className="fp-adv-grid">
              <DerivativeMarkets fixture={fixture} grid={grid} lambdas={lambdas} />
            </div>
          ) : (
            <div style={{ paddingTop: 12 }}>
              <PaywallCard
                requiredTier="sika_pro"
                featureName="Derivative markets"
                pitch="Price any derivative of this fixture — alternate Asian handicaps, alternate totals, and first-half 1X2 — all from STRIKZA's own scoreline distribution, no separate calculator. Available on STRIKZA Sika Pro."
              />
            </div>
          )}
        </div>

        <div className="fp-foot">
          <div className="fp-foot-note">
            Phase 1A deterministic estimate. Snapshot at preparation, not a live bookmaker feed.
            Manual placement remains outside STRIKZA.
          </div>
          <div className="fp-foot-actions">
            <button type="button" className="btn">
              <Icon name="star" size={13} />
              Watch
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
